import { type ChangeEvent, useCallback } from 'react'

import type { AccessRole, AccessUser, Me } from '@/api/types'
import { ICONS } from '@/components/icons/icons'
import { Badge, type Tone } from '@/components/ui/Badge'
import { formatDate } from '@/lib/format'

import styles from './Access.module.css'
import { assignableRoles, isSelf, lockReason, ROLE_LABEL } from './rules'

const ROLE_TONE: Record<AccessRole, Tone> = { admin: 'accent', manager: 'good', viewer: 'neutral' }

function initials(user: AccessUser): string {
  const words = user.name.trim().split(/\s+/).filter(Boolean)
  const letters =
    words.length > 1 ? `${words[0]![0]}${words[words.length - 1]![0]}` : (words[0] ?? user.email).slice(0, 2)
  return letters.toUpperCase()
}

function addedBy(user: AccessUser): string {
  if (!user.created_by) return 'just now'
  if (user.created_by.startsWith('system:')) return 'by setup'
  return `by ${user.created_by.split('@')[0]}`
}

interface RowProps {
  user: AccessUser
  me: Me
  busy: boolean
  onRoleChange: (user: AccessUser, role: AccessRole) => void
  onRemove: (user: AccessUser) => void
}

function AccessRow({ user, me, busy, onRoleChange, onRemove }: RowProps) {
  const removed = user.status === 'disabled'
  const pending = user.id.startsWith('pending:')
  const locked = lockReason(me, user)
  const editable = !removed && !pending && locked === null

  const handleRole = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => onRoleChange(user, event.target.value as AccessRole),
    [onRoleChange, user],
  )
  const handleRemove = useCallback(() => onRemove(user), [onRemove, user])

  return (
    <tr className={removed ? styles.removedRow : undefined}>
      <td>
        <div className={styles.person}>
          <span className={styles.avatar} aria-hidden="true">
            {initials(user)}
          </span>
          <div>
            <div className={styles.name}>
              {user.name}
              {isSelf(me, user) ? <span className={styles.you}>You</span> : null}
            </div>
            <div className={styles.email}>{user.email}</div>
          </div>
        </div>
      </td>
      <td>
        {editable ? (
          <select
            className={styles.roleSelect}
            value={user.role}
            onChange={handleRole}
            disabled={busy}
            aria-label={`Role for ${user.name}`}
          >
            {assignableRoles(me).map((role) => (
              <option key={role} value={role}>
                {ROLE_LABEL[role]}
              </option>
            ))}
          </select>
        ) : (
          <Badge tone={ROLE_TONE[user.role]} small>
            {ROLE_LABEL[user.role]}
          </Badge>
        )}
      </td>
      <td>
        {removed ? (
          <Badge tone="neutral" small>
            Removed
          </Badge>
        ) : pending ? (
          <Badge tone="warn" small>
            Adding…
          </Badge>
        ) : (
          <Badge tone="good" small>
            Active
          </Badge>
        )}
      </td>
      <td>
        <div className={styles.meta}>{formatDate(user.created_at)}</div>
        <div className={styles.metaSub}>{addedBy(user)}</div>
      </td>
      <td className={styles.actions}>
        {editable ? (
          <button
            type="button"
            className={styles.iconButton}
            onClick={handleRemove}
            disabled={busy}
            aria-label={`Remove access for ${user.name}`}
          >
            <ICONS.trash size={14} />
            Remove
          </button>
        ) : !removed && locked ? (
          <span className={styles.lock} title={locked}>
            <ICONS.lock size={12} /> <span className="visually-hidden">{locked}</span>
          </span>
        ) : null}
      </td>
    </tr>
  )
}

interface AccessTableProps {
  users: AccessUser[]
  me: Me
  busyId: string | null
  onRoleChange: (user: AccessUser, role: AccessRole) => void
  onRemove: (user: AccessUser) => void
}

export function AccessTable({ users, me, busyId, onRoleChange, onRemove }: AccessTableProps) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">Person</th>
            <th scope="col">Role</th>
            <th scope="col">Status</th>
            <th scope="col">Added</th>
            <th scope="col">
              <span className="visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <AccessRow
              key={user.id}
              user={user}
              me={me}
              busy={busyId === user.id}
              onRoleChange={onRoleChange}
              onRemove={onRemove}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}
